<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';

import { useSidebarContext } from './provider';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  isExpanded: { type: Boolean, default: false },
  label: { type: String, required: true },
  icon: { type: [Object, String], required: true },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
  collapsible: { type: Boolean, default: false },
  level: { type: Number, default: 1 },
});

const { isAllowed } = useSidebarContext();
const scrollableContainer = ref(null);

const hasAllowedDescendant = child => {
  if (!child) return false;
  if (child.to && isAllowed(child.to)) return true;
  if (!Array.isArray(child.children)) return false;

  return child.children.some(descendant => hasAllowedDescendant(descendant));
};

const accessibleItems = computed(() =>
  props.children.filter(child => {
    return hasAllowedDescendant(child);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

const hasActiveDescendant = child => {
  if (!child) return false;
  if (props.activeChild?.name === child.name) return true;
  if (!Array.isArray(child.children)) return false;

  return child.children.some(descendant => hasActiveDescendant(descendant));
};

const isScrollable = computed(() => {
  return accessibleItems.value.length > 7;
});

const hasActiveChildren = computed(() => {
  return accessibleItems.value.some(child => hasActiveDescendant(child));
});

const isNestedExpanded = ref(false);

watch(
  hasActiveChildren,
  hasActive => {
    if (hasActive) {
      isNestedExpanded.value = true;
    }
  },
  { immediate: true }
);

const showChildren = computed(() => {
  return props.collapsible ? isNestedExpanded.value : props.isExpanded;
});

const toggleNestedExpansion = () => {
  if (!props.collapsible) return;
  isNestedExpanded.value = !isNestedExpanded.value;
};

const scrollEnd = ref(false);

// set scrollEnd to true when the scroll reaches the end
useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});
</script>

<template>
  <div
    v-if="collapsible && hasAccessibleItems"
    v-show="isExpanded || hasActiveChildren"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 min-w-0"
  >
    <button
      type="button"
      class="flex w-full items-center gap-2 px-2 py-1 rounded-lg h-8 text-n-slate-11 hover:bg-n-alpha-2"
      :class="{
        'text-n-slate-12 font-medium bg-n-alpha-2': hasActiveChildren,
      }"
      @click="toggleNestedExpansion"
    >
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span class="min-w-0 flex-1 truncate text-left text-sm">
        {{ label }}
      </span>
      <span
        class="size-4 text-n-slate-10 transition-transform"
        :class="
          showChildren ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'
        "
      />
    </button>
  </div>
  <SidebarGroupSeparator
    v-else-if="hasAccessibleItems"
    v-show="isExpanded"
    :label
    :icon
    class="my-1"
  />
  <ul
    v-if="children.length"
    class="m-0 list-none reset-base relative group min-w-0"
  >
    <!-- Each element has h-8, which is 32px, we will show 7 items with one hidden at the end,
    which is 14rem. Then we add 16px so that we have some text visible from the next item  -->
    <div
      ref="scrollableContainer"
      class="min-w-0"
      :class="{
        'max-h-[calc(14rem+16px)] overflow-y-scroll no-scrollbar': isScrollable,
      }"
    >
      <template v-for="child in children" :key="child.name">
        <SidebarSubGroup
          v-if="child.children"
          v-show="showChildren || hasActiveDescendant(child)"
          :label="child.label"
          :icon="child.icon"
          :children="child.children"
          :is-expanded="showChildren"
          :active-child="activeChild"
          :collapsible="Boolean(child.collapsible)"
          :level="level + 1"
        />
        <SidebarGroupLeaf
          v-else
          v-show="showChildren || hasActiveDescendant(child)"
          v-bind="child"
          :active="hasActiveDescendant(child)"
          :nesting-level="level + 1"
        />
      </template>
    </div>
    <div
      v-if="isScrollable && showChildren"
      v-show="!scrollEnd"
      class="absolute bg-gradient-to-t from-n-background w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2 animate-fade-in-up"
    >
      <svg
        width="16"
        height="24"
        viewBox="0 0 16 24"
        fill="none"
        class="text-n-slate-9 opacity-50 group-hover:opacity-100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 4L8 8L12 4"
          stroke="currentColor"
          opacity="0.5"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 10L8 14L12 10"
          stroke="currentColor"
          opacity="0.75"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 16L8 20L12 16"
          stroke="currentColor"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>
